import { useEffect, useMemo, useState } from "react";
import useTypingText from "src/hooks/useTypingText";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "@clerk/clerk-react";
import { ArrowLeft, DotsThreeVertical, List, ShareFat } from "@phosphor-icons/react";
import { Loader } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { trackAnalytics } from "src/pages/helpers";
import { setShowChatActionModal } from "src/store/slices/threadsSlice";
import { setMobileSideNav, setOpenLandingPageMenu } from "src/store/slices/HomeSlice";
import DocsInChatToggle from "src/components/DocsInChatToggle/DocsInChatToggle";
import IHLogoLarge from "src/images/logos/inhouse-logo-large.png";
import TryPageTopNav from "./TryPageTopNav";
import SharedPageTopNav from "./SharedPageTopNav";
import DotsLoader from "src/components/DotsLoader";
import ShareThreadModalSecured from "src/pages/Modals/ShareThreadModalSecured";
import { openShareModal } from "src/store/slices/sharedThreadSlice";
import {
  BUTTON_ROLES,
  BUTTON_TYPES,
  EVENT_NAMES,
  EVENT_PARAMETERS,
  getPageName,
  MODAL_NAMES,
} from "src/utils/EventConstants";

const TopNav = ({ page }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn } = useAuth();

  const [text, setText] = useState("");
  const [duration, setDuration] = useState("");
  const [actionOnThread, setActionOnThread] = useState("");
  const [updateActionLoading, setUpdateActionLoading] = useState(false);

  const currentDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const options = { weekday: "long", day: "numeric", month: "short", year: "numeric" };
  const formattedDate = currentDate.toLocaleDateString("en-US", options);

  const threads = useSelector((state) => state.threads.threads);
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  const threadData = useSelector((state) => state.messageSlice.threadData);
  const threadTitleTop = useSelector((state) => state.messageSlice.threadTitleTop);
  const isSharedThreadPage = useSelector((state) => state.homeslice.isSharedThreadPage);
  const shareFetchStatus = useSelector((state) => state.sharedSlice?.fetchStatus);

  const shouldType = threadData?.messages?.length === 1;
  const typingInput = shouldType ? text : "";
  const { displayedText } = useTypingText(typingInput);
  const titleToShow = useMemo(() => (shouldType ? displayedText : text), [shouldType, displayedText, text]);

  // Effects
  useEffect(() => {
    if (page === "chat" && Object.keys(threads).length > 0) {
      var keyName = "";
      for (var key in threads) {
        var index = threads[key].find((thread) => thread.id === threadData.id);
        if (index) {
          keyName = key;
          break;
        }
      }
      if (keyName) {
        setDuration(key);
      }
    }
  }, [page, threads]); // eslint-disable-line

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        dispatch(setOpenLandingPageMenu(true));
      } else {
        dispatch(setOpenLandingPageMenu(false));
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch]);

  useEffect(() => {
    if (page !== "ask_home") {
      if (threadData?.title && threadData.title !== "PydanticUndefined" && threadData.title !== "New thread") {
        setText(threadData.title);
      } else if (threadTitleTop && threadTitleTop?.title) {
        setText(threadTitleTop.title);
      } else {
        // Title not ready yet — keep empty to show loading state
        setText("");
      }
    } else {
      setText(formattedDate);
    }
  }, [threadData]); // eslint-disable-line

  const shareThread = () => {
    dispatch(openShareModal({ sharedThreadId: threadData?.id, sharedThreadTitle: threadData?.title || threadData?.thread_title }));
    trackAnalytics(
      EVENT_NAMES.CLICKED_BUTTON,
      {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.THREAD_ID]: threadData?.id,
        [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
        [EVENT_PARAMETERS.BUTTON_TEXT]: "Share",
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.REQUEST,
        [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
        [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.TOP_NAV,
      },
      userMetadata,
    );

    // Old share flow removed — new ShareThreadModalSecured handles everything via sharedThreadSlice
  };

  const handleMobileSideNav = () => {
    dispatch(setMobileSideNav(true));
  };

  // If not authenticated, show appropriate nav
  if (!isSignedIn) {
    if (isSharedThreadPage) {
      return <SharedPageTopNav />;
    }
    return <TryPageTopNav page={page} />;
  }

  // Home page
  if (page === "home") {
    return (
      <div className="fixed top-0 w-full md:static flex flex-col z-[2]">
        <div className="grid grid-cols-3 md:flex md:justify-between items-center h-[54px] md:h-[74px] md:!shadow-none bg-[#FDFBF8] md:bg-transparent border-b border-[#E5E5E5] md:border-b-0 w-full mx-auto px-[12px] md:px-[28px] gap-[16px]">
          <span className="w-[24px] h-[24px] md:hidden">
            <List onClick={handleMobileSideNav} size={26} />
          </span>
          <div className="flex justify-center md:justify-start">
            <img src={IHLogoLarge} alt="Inhouse logo" className="w-[100px] h-[21px] object-contain md:hidden" draggable={false} />
          </div>
        </div>
      </div>
    );
  }

  // Document library page
  if (page === "document-library") {
    const cameFromChat = location.state?.from?.startsWith("/chat/");

    return (
      <div className="fixed top-0 w-full md:static flex flex-col z-[2]">
        <div className="flex justify-between items-center h-[54px] md:h-[74px] md:!shadow-none bg-[#FDFBF8] border-b border-[#E5E5E5] w-full mx-auto px-[12px] md:px-[28px] gap-[16px]">
          <span className="w-[24px] h-[24px] md:hidden">
            <List onClick={handleMobileSideNav} size={26} />
          </span>
          <div className="flex-1 flex items-center gap-[12px]">
            {cameFromChat && (
              <button
                onClick={() => navigate(-1)}
                className="hidden md:flex items-center justify-center w-[28px] h-[28px] rounded-full bg-[#F4F4F6] hover:bg-[#EAEAEC] focus:outline-none focus:ring-2 focus:ring-[#18475A] focus:ring-offset-1 transition-colors cursor-pointer"
                aria-label="Back to chat"
                tabIndex={0}
              >
                <ArrowLeft size={18} className="text-[#686868]" />
              </button>
            )}
            <p className="font-[600] font-sans text-[#000000] text-[16px]">Document Library</p>
          </div>
          {cameFromChat && (
            <button
              onClick={() => navigate(-1)}
              className="flex md:hidden items-center gap-[6px] px-[10px] py-[6px] rounded-full hover:bg-[#F4F4F6] focus:outline-none focus:ring-2 focus:ring-[#18475A] focus:ring-offset-1 transition-colors cursor-pointer"
              aria-label="Back to chat"
              tabIndex={0}
            >
              <ArrowLeft size={14} className="text-[#686868]" />
              <span className="text-[12px] font-medium text-[#686868]">Back to Chat</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Chat or Shared page
  if (page.toLowerCase() === "chat" || page.toLowerCase() === "shared") {
    return (
      <>
        <div className="fixed top-0 left-0 right-0 md:static flex flex-col z-[50]">
          <div className="flex items-center h-[54px] md:h-[74px] md:!shadow-none bg-[#FDFBF8] border-b border-[#E5E5E5] w-full max-w-[100vw] overflow-hidden box-border px-[12px] md:px-[28px] gap-[8px] md:gap-[16px]">
            <span className="w-[24px] h-[24px] flex-shrink-0 md:hidden">
              <List onClick={handleMobileSideNav} size={26} />
            </span>
            <div className="flex-1 min-w-0 overflow-hidden">
              {threadData?.id ? (
                titleToShow ? (
                  <p className="my-auto truncate font-['DM_Sans'] font-[500] text-[12px] md:text-[15px] md:leading-[22px] tracking-[0px] text-[#172921]">
                    {titleToShow}
                  </p>
                ) : (
                  <DotsLoader />
                )
              ) : null}
            </div>
            <div className="flex items-center gap-[8px] md:gap-[16px] flex-shrink-0 ml-auto">
              <div className="hidden md:flex gap-[20px] items-center">
                <DocsInChatToggle variant="desktop" pageName={getPageName()} />
                {page === "chat" && !isSharedThreadPage && (
                  <div className="relative" style={{ zIndex: 9999 }}>
                    <button
                      data-testid="share-thread-btn"
                      className="hidden md:flex items-center border border-[#E5E5E5] bg-white hover:bg-gray-50 transition-colors"
                      onClick={shareThread}
                      disabled={shareFetchStatus === "loading"}
                      style={{
                        minWidth: "86px",
                        height: "36px",
                        borderRadius: "9999px",
                        paddingTop: "6px",
                        paddingBottom: "6px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        gap: "6px",
                      }}
                    >
                      {shareFetchStatus === "loading" ? (
                        <Loader size={16} className="animate-spin text-[#242424]" />
                      ) : (
                        <ShareFat size={16} className="text-[#242424]" />
                      )}
                      <span style={{ fontFamily: "DM Sans", fontWeight: 500, fontSize: "14px", lineHeight: "100%", color: "#242424" }}>
                        {shareFetchStatus === "loading" ? "Generating..." : "Share"}
                      </span>
                    </button>
                    <ShareThreadModalSecured />
                  </div>
                )}
              </div>
              <DocsInChatToggle variant="mobile" pageName={getPageName()} />
              {page === "chat" && !isSharedThreadPage && (
                <div
                  className="md:hidden flex justify-center items-center border w-[30px] h-[30px] rounded-[6px] overflow-hidden"
                  onClick={() => dispatch(setShowChatActionModal(true))}
                >
                  <DotsThreeVertical size={20} />
                </div>
              )}
            </div>
          </div>
        </div>

      </>
    );
  }

  return null;
};

export default TopNav;
